import { useEffect, useState, useSyncExternalStore } from 'react';
import { getMySettings, patchMySettings, type UserSettingsDto } from '../../../api/users';
import { applyCustomTheme, themeFromSettings, isHex, type CustomTheme } from '../../../lib/theme-custom';
import {
  THEME_CATALOG, NEUTRALS, matchCatalog, paletteFor,
  DEFAULT_THEME_SWATCH, type CatalogTheme,
} from '../../../lib/theme-catalog';
import { useThemeStore } from '../../../store/themeStore';
import { isThemeMode, type ThemeMode } from '../../../styles/themes';
import { Check } from '../../../icons';
import {
  tSettings, getSettingsLang, subscribeSettingsLang, type SettingsI18nKey,
} from '../../../lib/settings-i18n';
import './ThemeSettingsPanel.css';

/**
 * 테마 설정 (2026-08-20) — 환경설정의 첫 화면. 옛 "화면·외관" 자리를 대신한다.
 *
 * 왜 바꿨나(2026-08-20 사용자): 색 동그라미 5개만 있으면 **고른 결과가 어떤 화면인지 모른다**.
 * 라이트·다크와 색을 미리 조합해 둔 **완성 테마 12종**을 작은 화면 그림과 함께 보여 주고,
 * 카드를 고르면 모드와 색이 한꺼번에 바뀐다. 직접 만들 사람은 아래 "테마 만들기" 로 간다.
 *
 * ★ 옛 화면(`AppearanceSection`)은 **손대지 않았다** — `/me` 페이지와 `PreferencesPanel` 이
 *   그대로 쓴다. 되돌릴 일이 생기면 SettingsModal 의 라우팅 한 줄만 되돌리면 된다.
 *
 * ★ 저장 자리를 새로 만들지 않는다. 카드 = 색 묶음이라 이미 있는 계정 칸(migration 106)에
 *   쓴다. 그래서 이 화면 때문에 서버가 바뀔 일은 없다.
 *
 * ★ 카드를 눌러도 **내가 따로 고른 라인색·표면색·모서리·속도는 유지한다** — 카드는 색 3개만
 *   덮는다. 안 그러면 테마 만들기에서 손본 것이 카드 한 번에 사라진다.
 */

const MODES: ReadonlyArray<{ mode: ThemeMode; labelKey: SettingsI18nKey }> = [
  { mode: 'light', labelKey: 'settings.appearance.mode.light' },
  { mode: 'dark', labelKey: 'settings.appearance.mode.dark' },
  { mode: 'auto', labelKey: 'settings.appearance.mode.auto' },
];

/**
 * 미리보기를 **부팅과 같은 식**으로 입힌다.
 *
 * ★ 고른 «직후» 화면과 새로고침 뒤 화면이 다르면 「저장이 안 됐다」로 읽힌다
 *   (2026-09-02 에 실제로 그렇게 보였다). 지금은 테마가 **개인 것뿐**이라 부팅도 이것과 같다.
 */
function previewLikeBoot(personal: CustomTheme | null): void {
  applyCustomTheme(personal);
}

export function ThemeSettingsPanel() {
  useSyncExternalStore(subscribeSettingsLang, getSettingsLang, getSettingsLang);
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);

  const [settings, setSettings] = useState<UserSettingsDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMySettings()
      .then((s) => {
        if (cancelled) return;
        setSettings(s);
        if (isThemeMode(s.themeMode)) setMode(s.themeMode);
      })
      .catch(() => { /* best-effort — 못 읽으면 지금 화면 그대로 둔다 */ });
    return () => { cancelled = true; };
    // store setter 는 안정 참조 — 마운트 1회
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = themeFromSettings(settings);
  const activeCard = matchCatalog({
    primary: current?.primary ?? null,
    accent: current?.accent ?? null,
    danger: current?.danger ?? null,
  });
  /** 「기본」이 켜져 있는가 — 개인이 색을 하나도 안 얹은 상태다. */
  const isDefault = !isHex(current?.primary ?? null);

  /**
   * 「기본」 고르기 — 얹었던 색을 **전부 비운다**(null). 그러면 앱 자신의 토큰이 돌아온다:
   * 라이트에서 레일·카드는 `--paper`(흰 종이), 바닥은 `--field`(옅은 회색).
   * ★ 모드(라이트/다크)는 건드리지 않는다 — 그건 색이 아니라 취향이라 아래 줄에서 따로 고른다.
   */
  async function pickDefault() {
    const prevTheme = current;
    const prevSettings = settings;
    const body = {
      themePrimary: null, themeAccent: null, themeDanger: null, themeLine: null,
      themeBg: null, themeRail: null, themeMenu: null, themeRrail: null, themeStatus: null,
      themeTable: null, themeTableHead: null, themeTableHeadText: null,
      themeIcon: null, themeIconHover: null, themeTint: null,
      themeText: null, themeTextMuted: null,
    } as const;
    // 개인 색을 비우면 «회사 기본 테마» 가 드러난다 — 부팅이 하는 것과 같게 그린다.
    previewLikeBoot(null);
    setSettings((s) => (s ? { ...s, ...body } : s));
    setError(null);
    try {
      await patchMySettings(body);
      setSavedAt(Date.now());
    } catch (err) {
      previewLikeBoot(prevTheme);
      setSettings(prevSettings);
      setError(tSettings('settings.env.err_save', {
        msg: err instanceof Error ? err.message : String(err),
      }));
    }
  }

  /**
   * 카드 고르기 — 모드와 색을 한 번에.
   *
   * ★ 2026-08-20 사용자 확정: 주색만 바꾸면 **레일·상태바·보드가 회색 그대로**라 테마를 바꿔도
   *   화면 절반이 안 갈아입는다. 그래서 카드마다 영역별 색을 주색에서 만들어 함께 얹는다
   *   (농도 규칙은 theme-catalog.ts `paletteFor`). 모서리·속도는 지금 것을 지킨다.
   */
  async function pick(card: CatalogTheme) {
    const pal = paletteFor(card);
    const next: CustomTheme = {
      ...(current ?? {}),
      primary: card.primary,
      accent: card.accent,
      danger: card.danger,
      line: pal.line,
      bg: pal.bg,
      rail: pal.rail,
      menu: pal.menu,
      rrail: pal.rrail,
      status: pal.status,
      table: pal.table,
      tableHead: pal.tableHead,
      tableHeadText: pal.tableHeadText,
      icon: pal.icon,
      iconHover: pal.iconHover,
      tint: pal.tint,
      text: pal.text,
      textMuted: pal.textMuted,
    };
    // 실패하면 되돌릴 수 있게 지금 것을 먼저 들고 있는다 — 저장이 안 됐는데 화면만 바뀌어
    // 있으면, 다음에 열었을 때 색이 되돌아가 있어 "저장이 풀렸다" 로 읽힌다.
    const prevTheme = current;
    const prevSettings = settings;
    const prevMode = mode;

    previewLikeBoot(next);  // 즉시 화면 — 부팅과 같은 합성
    setMode(card.mode);              // 카드의 라이트/다크로 함께 넘어간다
    const body = {
      themeMode: card.mode,
      themePrimary: card.primary,
      themeAccent: card.accent,
      themeDanger: card.danger,
      themeLine: pal.line,
      themeBg: pal.bg,
      themeRail: pal.rail,
      themeMenu: pal.menu,
      themeRrail: pal.rrail,
      themeStatus: pal.status,
      themeTable: pal.table,
      themeTableHead: pal.tableHead,
      themeTableHeadText: pal.tableHeadText,
      themeIcon: pal.icon,
      themeIconHover: pal.iconHover,
      themeTint: pal.tint,
      themeText: pal.text,
      themeTextMuted: pal.textMuted,
    } as const;
    setSettings((s) => (s ? { ...s, ...body } : s));
    setError(null);
    try {
      await patchMySettings(body);
      setSavedAt(Date.now());
    } catch (err) {
      previewLikeBoot(prevTheme);
      setMode(prevMode);
      setSettings(prevSettings);
      setError(tSettings('settings.env.err_save', {
        msg: err instanceof Error ? err.message : String(err),
      }));
    }
  }

  async function pickMode(next: ThemeMode) {
    setMode(next);                   // 즉시 화면(auto 는 OS 를 따른다)
    setSettings((s) => (s ? { ...s, themeMode: next } : s));
    setError(null);
    try {
      await patchMySettings({ themeMode: next });
      setSavedAt(Date.now());
    } catch (err) {
      setError(tSettings('settings.env.err_save', {
        msg: err instanceof Error ? err.message : String(err),
      }));
    }
  }

  return (
    <section className="settings-section" aria-label={tSettings('settings.themeset.heading')}>
      <h2>{tSettings('settings.themeset.heading')}</h2>
      <p className="settings-section-subtitle">{tSettings('settings.themeset.subtitle')}</p>

      <div className="ts-grid" role="radiogroup" aria-label={tSettings('settings.themeset.heading')}>
        {/* 「기본」 — 색을 얹지 않는 상태. 이 카드가 없어서 오션 블루가 기본값처럼 보였다. */}
        <button
          type="button"
          role="radio"
          aria-checked={isDefault}
          className={`ts-card${isDefault ? ' is-on' : ''}`}
          onClick={() => { void pickDefault(); }}
        >
          <span
            className="ts-thumb"
            style={{ background: DEFAULT_THEME_SWATCH[1], borderColor: DEFAULT_THEME_SWATCH[2] }}
            aria-hidden
          >
            <span
              className="ts-thumb__rail"
              style={{ background: DEFAULT_THEME_SWATCH[0], borderColor: DEFAULT_THEME_SWATCH[2] }}
            >
              <i style={{ background: DEFAULT_THEME_SWATCH[2] }} />
              <i style={{ background: DEFAULT_THEME_SWATCH[2] }} />
              <i style={{ background: DEFAULT_THEME_SWATCH[2] }} />
            </span>
            <span className="ts-thumb__main">
              <span className="ts-thumb__head">
                <i style={{ background: DEFAULT_THEME_SWATCH[2] }} />
                <b style={{ background: DEFAULT_THEME_SWATCH[2] }} />
              </span>
              <span
                className="ts-thumb__row"
                style={{ background: DEFAULT_THEME_SWATCH[0], borderColor: DEFAULT_THEME_SWATCH[2] }}
              >
                <i style={{ background: DEFAULT_THEME_SWATCH[2] }} />
                <i style={{ background: DEFAULT_THEME_SWATCH[2] }} />
              </span>
              <span
                className="ts-thumb__row"
                style={{ background: DEFAULT_THEME_SWATCH[0], borderColor: DEFAULT_THEME_SWATCH[2] }}
              >
                <i style={{ background: DEFAULT_THEME_SWATCH[2] }} />
                <i style={{ background: DEFAULT_THEME_SWATCH[2] }} />
              </span>
            </span>
          </span>
          <span className="ts-card__name">{tSettings('settings.theme.p_default')}</span>
          <span className="ts-card__mode">{tSettings('settings.theme.p_default_desc')}</span>
        </button>

        {THEME_CATALOG.map((card) => {
          const on = activeCard?.id === card.id;
          const n = NEUTRALS[card.mode];
          return (
            <button
              key={card.id}
              type="button"
              role="radio"
              aria-checked={on}
              className={`ts-card${on ? ' is-on' : ''}`}
              onClick={() => { void pick(card); }}
            >
              {/* 작은 화면 그림 — 색만 바꾼 같은 얼개라, 카드끼리 비교가 색 차이로만 보인다. */}
              <span className="ts-thumb" style={{ background: n.bg, borderColor: n.border }} aria-hidden>
                <span className="ts-thumb__rail" style={{ background: n.surface, borderColor: n.border }}>
                  <i style={{ background: card.primary }} />
                  <i style={{ background: n.border }} />
                  <i style={{ background: n.border }} />
                </span>
                <span className="ts-thumb__main">
                  <span className="ts-thumb__head">
                    <i style={{ background: n.border }} />
                    <b style={{ background: card.primary }} />
                  </span>
                  <span className="ts-thumb__row" style={{ background: n.surface, borderColor: n.border }}>
                    <i style={{ background: card.accent }} />
                    <i style={{ background: n.border }} />
                  </span>
                  <span className="ts-thumb__row" style={{ background: n.surface, borderColor: n.border }}>
                    <i style={{ background: card.danger }} />
                    <i style={{ background: n.border }} />
                  </span>
                </span>
              </span>

              <span className="ts-card__meta">
                <span className="ts-card__name">{tSettings(card.nameKey)}</span>
                <span className="ts-card__mode">
                  {tSettings(card.mode === 'dark' ? 'settings.appearance.mode.dark' : 'settings.appearance.mode.light')}
                </span>
              </span>

              {on && <span className="ts-card__chk" aria-hidden><Check size={12} /></span>}
            </button>
          );
        })}
      </div>

      <div className="ts-moderow">
        <span className="ts-moderow__label">
          <b>{tSettings('settings.appearance.label_mode')}</b>
          <span>{tSettings('settings.themeset.mode_desc')}</span>
        </span>
        <div className="settings-mode-toggle" role="radiogroup" aria-label={tSettings('settings.appearance.label_mode')}>
          {MODES.map((m) => (
            <button
              key={m.mode}
              type="button"
              role="radio"
              aria-checked={mode === m.mode}
              className={`settings-mode-option${mode === m.mode ? ' selected' : ''}`}
              onClick={() => { void pickMode(m.mode); }}
            >
              {tSettings(m.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {mode === 'auto' && <p className="settings-section__hint">{tSettings('settings.appearance.mode.auto_hint')}</p>}
      <p className="ts-hint">{tSettings('settings.themeset.custom_hint')}</p>

      {error && <p className="settings-error" role="alert">{error}</p>}
      {savedAt && !error && (
        <p className="settings-success"><Check size={14} aria-hidden /> {tSettings('settings.msg.saved')}</p>
      )}
    </section>
  );
}
